import { IUpdateExercisesApiResponse } from "../api/exercises/exercises.types";

export enum RoutesLocaleType {
  en = "en",
  uk = "uk",
  fr = "fr",
  es = "es",
  it = "it",
  de = "de",
  pt_BR = "pt_BR",
  tr = "tr",
  nl = "nl",
  ru = "ru",
}

export interface AppStoreType {
  locale: `${Locales}`;
  keyboardLayout: keyof Keyboards;
  activeCharacterName: CharactersTypes | null;
  unAuthFirstString: string;
  adsData: AdsData;
  speedRatio: number;
  showResult: boolean;
  currentCoins: number;
  showKeyboard: boolean;
  showHands: boolean;
  diacriticActive: boolean;
  deadKey: string;
  errorKey: string;
  typeKeyboard: string;
  metaKey: number | null;
  metaKeys: number[];
  progress: number;
  isAuthorized: boolean;
  token: string | null;
  userData: UserData;
  characters: ICharacter[];
  isAdsScriptsInjected: boolean;
  isAdsScriptsReloaded: boolean;
  currentAdsPage: string;
  adsForceRefresh: boolean;
}

export type InputFieldData = {
  text: string;
  textUnauthorized: string;
};

export type PropsTypeSvg = {
  classNameProps?: string;
  height?: number;
  width?: number;
  colorFill?: string;
  active?: boolean;
};

export type PropsTypeElemIndicatorSVG = {
  classNameProps?: string;
  d: string;
  colorPathCircle: string;
  height?: number;
  width?: number;
  strokeDashoffset: number;
};

export type PropsTypeSelect = {
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  id: string;
  title: string;
  arr: undefined | ArrSelectType[];
};

export type ArrSelectType = {
  value: string | number;
  text: string;
};

export type UserData = {
  id: number | null;
  email: string;
  name: string;
  courseName: string;
  locale: string;
  courseId: number | null;
  coins: number;
  lessonId: number | null;
  previousExerciseId: number | null;
  lastExerciseId: number | null;
  lastLessonId: number | null;
  currentLessonId: number | null;
  currentExerciseId: number | null;
  layout: string;
  layoutLeftHand: string;
  layoutRightHand: string;
  lastExercise: number | null;
  lastLesson: number | null;
  currentExercise: number | null;
  nextLesson: number | null;
  regDatetime: string;
  gameMode: boolean;
  showKeyboard: boolean;
};

export type AdsData = {
  isLoaded: boolean;
  tags: AdsTags;
  scripts: AdsScripts;
};

export type AdsTags = {
  testString: string;
  testStringResult: string;
  result: string;
  mainBottom: string;
  mainLeft: string;
  mainRight: string;
  mainFullWidth: string;
  finishRight: string;
  finishLeft: string;
  finishBottom: string;
  challengeBottom: string;
};

export type AdsScripts = {
  load: string;
  refresh: string;
};

export type CoursesType = {
  id: number;
  name: string;
  orderId: number;
  layout: string;
  layoutName: string;
  language: string;
  layoutLeftHand: string;
  layoutRightHand: string;
  activeLast: boolean;
};

export type LessonsType = {
  id: number;
  course_id: number;
  order_id: number;
  name: string;
  advice: string;
  active: boolean;
  end: boolean;
};

export type ExercisesType = {
  id: number;
  lesson_id: number;
  order_id: number;
  name: string;
  string: string;
  error_limit: number;
  speed_limit: number;
  star2_speed: number;
  star2_error: number;
  star3_speed: number;
  star3_error: number;
  active: boolean;
  star: boolean;
  target: boolean;
  lightning: boolean;
  star_coin: number;
  target_coin: number;
  lightning_coin: number;
};

export type CharactersType = {
  id: number;
  cost: number;
  currencyCost: number;
  name: string;
  paymentForm: string;
  purchased: true;
  selected: true;
  active: true;
};

export type Award = {
  speed: number;
  error: number;
  earned: boolean;
  coin: number;
  received: boolean;
};

export type Keyboards = {
  qwerty: Keyboard;
  dvorak: Keyboard;
  russian: Keyboard;
  ukrainian: Keyboard;
  "ukrainian-mac": Keyboard;
  azerty: Keyboard;
  qwerty_es: Keyboard;
  qwerty_it: Keyboard;
  qwerty_de: Keyboard;
  qwerty_pt_br: Keyboard;
  qwerty_pl: Keyboard;
  qwerty_tr: Keyboard;
  qwerty_nl: Keyboard;
};

export type KeyboardModifications =
  | "default"
  | "shift"
  | "alt"
  | "alt-shift"
  | "`"
  | "`-shift"
  | "~"
  | "~-shift"
  | "´"
  | "´-shift"
  | "^"
  | "^-shift";

export type Keyboard = {
  name: string;
  default: string[];
  shift: string[];
  alt?: string[];
  "alt-shift"?: string[];
  "`"?: string[];
  "`-shift"?: string[];
  "~"?: string[];
  "~-shift"?: string[];
  "´"?: string[];
  "´-shift"?: string[];
  "^"?: string[];
  "^-shift"?: string[];
  // diacritics?: string[];
  // "diacritics-apostrophe"?: string[];
  // "diacritics-apostrophe-shift"?: string[];
  // "diacritics-shift"?: string[];
  colors: string[];
  deadKey?: string;
  acceptedKeys?: string;
  acceptedWithDeadKey?: string;
  specialKeyNames: SpecialKeyNames;
  deadKeySpecialKeyNames?: DeadKeySpecialKeyNames;
  deadKeyElements?: DeadKeyElements;
  shiftElements?: string;
  shiftAltElements?: string;
  altElements?: string;
};

export type SpecialKeyNames = {
  shift: string;
  enter: string;
  lock: string;
  tab: string;
  bksp: string;
  space: string;
  alt?: string;
};

export type DeadKeySpecialKeyNames = {
  BracketLeft?: string;
  ShiftBracketLeft?: string;
  Quote?: string;
  ShiftQuote?: string;
};

export type DeadKeyElements = {
  "´"?: string;
  "^"?: string;
  "`"?: string;
  "~"?: string;
};

export type ExerciseCompleteData = {
  blockExercise: boolean;
  id: number;
  courseImg: string;
  currentLessonId: number;
  nextExerciseId: number;
  lastExerciseId: number;
  courseId: number;
  coinsBeforeStart: number;
  status: string;
  starData: Award;
  targetData: Award;
  lightningData: Award;
  exercises: IUserExercise[];
  lessons: ILesson[];
  courses: ICourse[];
  speed: number;
  mistake: number;
  currentCoins: number;
  coins: number;
  gameMode: boolean;
  speedRatio: number;
  lastLesson: ILesson | null;
  lastCourse: ICourse | null;
  rewards: IReward[];
  lessonCoins: number;
};

export type LessonCompleteData = {
  lessons: ILesson[];
  exercises: IUserExercise[];
  coins: number;
  lessonNumber: number;
  currentLessonId: number;
  gameMode: boolean;
  courseId: number;
};

export type CourseCompleteData = {
  courseTitle: string;
  courseId: number;
};

export type TestStringResults = {
  speed: number;
  speedRatio: number;
};

export type CharactersTypes =
  | "packo"
  | "cato"
  | "froga"
  | "dragy"
  | "alba"
  | "pingo"
  | "kito"
  | "uni"
  | "solo"
  | "rufy"
  | "beavy"
  | "bunny"
  | "dino";
export interface ICharacterData {
  lottieFile: Record<string, unknown>;
  gif: string;
  timing: {
    inaction: [number, number];
    bite: [number, number];
    jump: [number, number];
    error: [number, number];
  };
  // style: {
  //   width: string;
  //   height: string;
  // };
}

export type CharactersConfig = Record<CharactersTypes, ICharacterData>;

export interface IUser {
  host: string;
  blockExercise: boolean;
  id: number;
  serverSessionIndex: number;
  plus: boolean;
  numAvailableExercises: number;
  email: string;
  name: string;
  locale: string;
  regDatetime: string;
  gameMode: boolean;
  showKeyboard: boolean;
  coins: number;
  courseId: number;
  courseName: string;
  courseLanguage: string;
  previousLessonId: number;
  currentLessonId: number;
  currentLesson: number;
  lastLessonId: number;
  lastLesson: number;
  previousExerciseId: number;
  currentExerciseId: number;
  currentExercise: number;
  lastExerciseId: number;
  lastExercise: number;
  layout: keyof Keyboards;
  layoutLeftHand: string;
  layoutRightHand: string;
  isSale: boolean;
  strixExercise: number;
  strixToday: boolean;
  isPromotion: boolean;
  prices: {
    character: string;
    course_cert: string;
    donate: string;
    license: string;
    remove_ads: string;
    subscribe: string;
  };
  rewards: IReward[];
}

export interface IReward {
  id: RewardEnum;
  name: string;
  description: string;
  code: string;
}

export enum RewardEnum {
  THREE_EX_LIGHT = 1,
  THREE_EX_TARGET,
  THREE_EX_STAR,
  FIRST_LESSON,
  FIRST_TEST,
  TEST_UPPER_35_WPM,
  TEST_100_ACCURACY,
  HERO_5,
  STRIX_7_DAY,
  REWARDS_9,
}

export interface ICharacter {
  id: number;
  cost: number;
  currencyCost: number;
  name: CharactersTypes;
  paymentForm: string;
  purchased: true;
  selected: true;
  active: true;
}

export interface IAward {
  speed: number;
  error: number;
  earned: boolean;
  coin: number;
  received: boolean;
}

export interface ICourse {
  id: number;
  name: string;
  orderId: number;
  layout: string;
  layoutName: string;
  language: string;
  layoutLeftHand: string;
  layoutRightHand: string;
  exercisesCount: number;
  lessonsCount: number;
  completedLessonsCount: number;
  completedExercisesCount: number;
  currentExerciseNumber: number | null;
  isStartedByUser: boolean;
  isCompletedByUser: boolean;
}

export interface ILesson {
  id: number;
  courseId: number;
  orderId: number;
  name: string;
  advice: string;
  isLessonActive: boolean;
  isLessonComplete: boolean;
  exercisesCount: number;
  exercisesWithStarCount: number;
  exercisesWithTargetCount: number;
  exercisesWithLightningCount: number;
  exercises: IUserExercise[];
}

export interface IExercise {
  id: number;
  lessonId: number;
  orderId: number;
  name: string;
  string: string;
  errorLimit: number;
  speedLimit: number;
  star2Speed: number;
  star2Error: number;
  star3Speed: number;
  star3Error: number;
  lessonAdvice: string;
}

export interface IUserExercise extends IExercise {
  isLastString: boolean;
  active: boolean;
  star: boolean;
  target: boolean;
  lightning: boolean;
}

export enum Locales {
  "de_DE" = "de-DE",
  "en_US" = "en-US",
  "en_GB" = "en-GB",
  "es_ES" = "es-ES",
  "es_MX" = "es-MX",
  "fr_CA" = "fr-CA",
  "fr_FR" = "fr-FR",
  "it_IT" = "it-IT",
  "nl_NL" = "nl-NL",
  "pl_PL" = "pl-PL",
  "pt_BR" = "pt-BR",
  "ru_RU" = "ru-RU",
  "tr_TR" = "tr-TR",
  "uk_UA" = "uk-UA",
}

export interface IExerciseComplete extends IUpdateExercisesApiResponse {
  speed: number;
  mistake: number;
  speedRatio: number;
}

export enum HelperModalType {
  "enabledCapslock" = 1,
  "disabledShift" = 2,
  "wrongLanguage",
  "removeAds",
}

export type KeyPressType = {
  value: string;
  isNumeric: boolean;
  isLetter: boolean;
  isUpperCase: boolean;
  isPrintable: boolean;
  isDeadKey: boolean;
  keyCode: number;
  containsShiftKey: boolean;
  containsCtrlKey: boolean;
  containsAltKey: boolean;
  containsMetaKey: boolean;
  containsCapslock: boolean;
  shouldIgnore: boolean;
};

export type RewardModalContent = {
  heading: string;
  text: string;
  nameImg: string;
};
